import React from 'react';

import type { Agent } from '@/hooks/useAgents';
import { SettingActionCard } from '@/sections/settings/components';

import { HealthStatusIcon } from './HealthStatusIcon';

interface CloudflareTunnelStatusProps {
  cloudflaredStatus?: NonNullable<Agent['health_check']>['cloudflared_status'];
}

type CloudflareHealthStatus = 'success' | 'error' | 'unknown';

/**
 * Determine health status from cloudflared status
 */
const getCloudflareHealthStatus = (
  cloudflaredStatus: CloudflareTunnelStatusProps['cloudflaredStatus']
): CloudflareHealthStatus => {
  if (!cloudflaredStatus) return 'unknown';

  const isRunning = cloudflaredStatus.status === 'running';
  const isConnected = cloudflaredStatus.connected === 'true';

  if (isRunning && isConnected) return 'success';
  return 'error';
};

/**
 * Get status message for display
 */
const getStatusMessage = (
  cloudflaredStatus: CloudflareTunnelStatusProps['cloudflaredStatus']
): string => {
  if (!cloudflaredStatus) return 'Tunnel status unavailable';

  const isRunning = cloudflaredStatus.status === 'running';
  const isConnected = cloudflaredStatus.connected === 'true';

  if (isRunning && isConnected) {
    return `Tunnel is connected (${cloudflaredStatus.connection_count || 0} active connections)`;
  }
  if (isRunning) return 'Tunnel is running but not connected';
  return 'Tunnel is not running';
};

/**
 * Cloudflare Tunnel status display for Health tab
 *
 * Follows SettingActionCard pattern matching other health checks.
 * Shows tunnel connectivity status with PRAETORIAN ONLY badge.
 */
export const CloudflareTunnelStatus: React.FC<CloudflareTunnelStatusProps> = ({
  cloudflaredStatus,
}) => {
  if (!cloudflaredStatus) return null;

  const status = getCloudflareHealthStatus(cloudflaredStatus);
  const message = getStatusMessage(cloudflaredStatus);

  return (
    <SettingActionCard
      action={<HealthStatusIcon status={status} />}
      data-testid="cloudflare-tunnel-status"
    >
      <div className="flex flex-col gap-1">
        <h4 className="font-bold">Cloudflare Tunnel</h4>
        <span className="text-chariot-text-secondary text-xs">{message}</span>
      </div>
    </SettingActionCard>
  );
};
